import React from 'react';
import styled from 'styled-components';
import { FiUser, FiCheck, FiStar } from 'react-icons/fi';
import colors from '../styles/colors';
import usePageMeta from '../hooks/usePageMeta';

const PageWrapper = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  padding: 80px 20px;
  background: linear-gradient(135deg, ${colors.background.darker} 0%, ${colors.background.medium} 100%);
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const Label = styled.div`
  font-family: 'Oswald', sans-serif;
  font-size: 0.9rem;
  color: ${colors.accent.main};
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 15px;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 6vw, 4rem);
  color: ${colors.text.heading};
  margin-bottom: 20px;

  span {
    color: ${colors.accent.main};
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: ${colors.text.light};
  line-height: 1.7;
`;

const Section = styled.section`
  padding: 100px 20px;
  background: ${props => props.$dark ? colors.background.dark : colors.background.medium};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const OwnerSection = styled.div`
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 60px;
  align-items: start;
  margin-bottom: 80px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: ${colors.metallic.silver};
    border-radius: 18px;
    z-index: -1;
  }

  @media (max-width: 900px) {
    max-width: 350px;
    margin: 0 auto;
  }
`;

const OwnerImage = styled.div`
  width: 100%;
  height: 400px;
  background: ${colors.background.card};
  border-radius: 16px;
  border: 2px solid ${colors.borderLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.muted};

  svg {
    font-size: 4rem;
  }
`;

const Content = styled.div``;

const OwnerName = styled.h2`
  font-size: 2rem;
  color: ${colors.text.heading};
  margin-bottom: 8px;
`;

const OwnerRole = styled.p`
  font-size: 1rem;
  color: ${colors.accent.main};
  font-weight: 600;
  margin-bottom: 24px;
`;

const Bio = styled.div`
  p {
    font-size: 1.05rem;
    color: ${colors.text.light};
    line-height: 1.8;
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const TestimonialsSection = styled.div`
  margin-bottom: 80px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 2.8rem);
  color: ${colors.text.heading};
  margin-bottom: 15px;

  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: ${colors.metallic.silver};
    margin: 20px auto 0;
    border-radius: 2px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${colors.text.light};
  max-width: 600px;
  margin: 0 auto;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 30px;
`;

const TestimonialCard = styled.div`
  background: ${colors.background.card};
  padding: 35px;
  border-radius: 12px;
  border: 1px solid ${colors.borderLight};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: ${colors.primary.dark};
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  }
`;

const Stars = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 20px;

  svg {
    color: ${colors.accent.main};
    font-size: 1.2rem;
  }
`;

const Quote = styled.p`
  font-size: 1rem;
  color: ${colors.text.light};
  line-height: 1.8;
  margin-bottom: 20px;
  font-style: italic;
`;

const Author = styled.div``;

const AuthorName = styled.div`
  font-family: 'Oswald', sans-serif;
  font-size: 1rem;
  color: ${colors.text.heading};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const AuthorRole = styled.div`
  font-size: 0.85rem;
  color: ${colors.text.muted};
`;

const PolicySection = styled.section`
  padding: 100px 20px;
  background: ${colors.background.card};
`;

const PolicyContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const PolicyGrid = styled.div`
  display: grid;
  gap: 25px;
`;

const PolicyCard = styled.div`
  background: ${colors.background.medium};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  padding: 30px;
`;

const PolicyTitle = styled.h3`
  font-size: 1.2rem;
  color: ${colors.text.heading};
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: ${colors.accent.main};
  }
`;

const PolicyText = styled.p`
  font-size: 1rem;
  color: ${colors.text.light};
  line-height: 1.8;
`;

const PolicyList = styled.ul`
  margin-top: 15px;
`;

const PolicyItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: ${colors.text.light};
  font-size: 0.95rem;
  line-height: 1.7;
  margin-bottom: 10px;

  svg {
    color: ${colors.accent.main};
    margin-top: 4px;
    flex-shrink: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const testimonials = [
  {
    quote: "Needed tires changed but my regular place was booked for weeks. Couldn't find anywhere until a friend mentioned Dave. I've been bringing my car here ever since. He does good work and I love supporting local businesses.",
    name: "Nin Kasi",
    role: "Google Review"
  },
  {
    quote: "David does a great job! He is fast, competent and less expensive than most. Also offers great customer service!! Was more than pleased with his work.",
    name: "Bonnie Daigneault",
    role: "Google Review"
  },
  {
    quote: "Nice Guy, and helped me out with an unexpected quick fix. Changed over my tires to snows real efficiently too. Would recommend.",
    name: "Anthony Pellegrino",
    role: "Google Review"
  },
];

export default function AboutPage() {
  usePageMeta(
    'About Us | Honest Auto Repair in Charlotte, VT | The Honest Mechanic',
    'Meet the team behind The Honest Mechanic in Charlotte, VT. We believe in transparent pricing, honest recommendations, and treating every car like our own.'
  );

  return (
    <PageWrapper>
      <HeroSection>
        <HeroContent>
          <Label>Our Story</Label>
          <Title>
            About <span>The Honest Mechanic</span>
          </Title>
          <Subtitle>
            We're your neighbors in Charlotte, VT — the only shop in town — and we're committed
            to honest work and fair prices for everyone who comes through our doors.
          </Subtitle>
        </HeroContent>
      </HeroSection>

      <Section>
        <Container>
          <OwnerSection>
            <ImageWrapper>
              <OwnerImage>
                <FiUser />
              </OwnerImage>
            </ImageWrapper>
            <Content>
              <OwnerName>Owner Name</OwnerName>
              <OwnerRole>Founder & Lead Mechanic</OwnerRole>
              <Bio>
                <p>
                  Content coming soon. The owner is working on writing up their story
                  and what drives their passion for honest, quality auto repair in Charlotte, VT.
                </p>
                <p>
                  Check back soon to learn more about the person behind The Honest Mechanic
                  and their commitment to serving the community with integrity.
                </p>
              </Bio>
            </Content>
          </OwnerSection>

          <TestimonialsSection>
            <SectionHeader>
              <SectionTitle>What Our Neighbors Say</SectionTitle>
              <SectionSubtitle>
                Don't just take our word for it. Here's what folks in the community have to say.
              </SectionSubtitle>
            </SectionHeader>
            <TestimonialsGrid>
              {testimonials.map((t, i) => (
                <TestimonialCard key={i}>
                  <Stars>
                    {[...Array(5)].map((_, j) => <FiStar key={j} fill={colors.accent.main} />)}
                  </Stars>
                  <Quote>"{t.quote}"</Quote>
                  <Author>
                    <AuthorName>{t.name}</AuthorName>
                    <AuthorRole>{t.role}</AuthorRole>
                  </Author>
                </TestimonialCard>
              ))}
            </TestimonialsGrid>
          </TestimonialsSection>
        </Container>
      </Section>

      <PolicySection>
        <PolicyContainer>
          <SectionHeader>
            <SectionTitle>Shop Policies</SectionTitle>
            <SectionSubtitle>
              We believe in transparency and treating every customer right. Here's what you can expect from us.
            </SectionSubtitle>
          </SectionHeader>

          <PolicyGrid>
            <PolicyCard>
              <PolicyTitle><FiCheck /> Estimates & Pricing</PolicyTitle>
              <PolicyText>
                We provide upfront, honest estimates before any work begins. No hidden fees, no surprise charges.
                If we discover additional issues during repair, we'll always call you first to discuss options and costs.
              </PolicyText>
            </PolicyCard>

            <PolicyCard>
              <PolicyTitle><FiCheck /> Our Promise</PolicyTitle>
              <PolicyList>
                <PolicyItem><FiCheck /> We'll explain what's wrong without the confusing jargon</PolicyItem>
                <PolicyItem><FiCheck /> We only recommend repairs you actually need</PolicyItem>
                <PolicyItem><FiCheck /> Your car will be treated with respect</PolicyItem>
                <PolicyItem><FiCheck /> We stand behind our work</PolicyItem>
              </PolicyList>
            </PolicyCard>

            <PolicyCard>
              <PolicyTitle><FiCheck /> Payment & Scheduling</PolicyTitle>
              <PolicyText>
                Payment is due upon completion of service. We accept cash, check, and all major credit cards.
                For scheduling, please call or use the contact form. We'll do our best to accommodate your schedule.
              </PolicyText>
            </PolicyCard>
          </PolicyGrid>
        </PolicyContainer>
      </PolicySection>
    </PageWrapper>
  );
}
